/**
 * WaitlistForm — pre-launch demand capture (Part D, 2026-07-21).
 *
 * Mounts under `#waitlist` on the landing page (Part C's customer
 * CTA scrolls to this anchor). Independent from the Contest B
 * marketing opt-in — DO NOT share component or state with
 * `ContestEnterPage`.
 *
 * Load-bearing invariants:
 *
 *   1. GUARD 17 (frontend leg) — the marketing checkbox state
 *      variable `waitlistMarketingOptIn` is initialized to `false`
 *      via a LITERAL `useState(false)`. It is NEVER coupled to any
 *      other form field's state; a coupling regression trips the
 *      source-scan guard in
 *      `WaitlistForm.marketingOptIn.driftGuard.test.jsx`.
 *
 *   2. Marketing checkbox label reads EXACTLY:
 *        "Email me PooPluckers news and offers"
 *      (Same wording as Contest B — brand consistency + Guard-17-
 *      symmetric.) No `(optional)` suffix — the unchecked-by-default
 *      state IS the optionality signal.
 *
 *   3. Turnstile — placeholder-mode graceful (verify_turnstile
 *      helper returns success=true when the secret is unset), so
 *      the form works in preview without a real widget.
 *
 *   4. Confirmation state echoes back the ZIP only. No PII.
 *      Message never promises a launch date (dispatch's honest-
 *      framing rule).
 */
import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

const LOCKED_MARKETING_LABEL = "Email me PooPluckers news and offers";

/**
 * Turnstile helper — mirrors the pattern used in
 * `pages/partner/BusinessIntakePage.jsx::isTurnstileConfigured`.
 * When `REACT_APP_TURNSTILE_SITE_KEY` is unset or holds the
 * pre-provisioning sentinel, we return `false` and the form
 * degrades gracefully: it renders the same "widget will appear
 * here once provisioned" placeholder box the intake form uses,
 * and posts the placeholder token to the backend (which accepts
 * it under `verify_turnstile`'s placeholder-mode path).
 *
 * When a real key IS configured, this signal STILL returns true
 * even though the real Cloudflare widget isn't mounted yet — an
 * upgrade to actually inject the `<div class="cf-turnstile">`
 * mount + `challenges.cloudflare.com/turnstile/v0/api.js` script
 * is a single follow-up dispatch after the key is provisioned in
 * production; both intake AND waitlist surfaces will graduate
 * together in that dispatch.
 */
function isTurnstileConfigured() {
  const k = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
  return Boolean(k) && k !== "PLACEHOLDER_UNTIL_PROVISIONED";
}

// Turnstile placeholder — when the operator hasn't provisioned a
// real site key, we submit a benign token string and the backend's
// `verify_turnstile` helper skips verification cleanly.
const TURNSTILE_SITE_KEY =
  process.env.REACT_APP_TURNSTILE_SITE_KEY || "";

export default function WaitlistForm() {
  // Guard 17 (frontend leg): waitlistMarketingOptIn is initialized
  // to `false` via a LITERAL useState(false). NEVER couple this to
  // any other form field's setter. Source-scan drift-guard pins the
  // exact declaration + forbids setter-coupling in
  // `WaitlistForm.marketingOptIn.driftGuard.test.jsx`.
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [zip, setZip] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [waitlistMarketingOptIn, setWaitlistMarketingOptIn] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [inlineError, setInlineError] = useState("");

  const emailIsValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
  const zipIsValid = /^\d{5}$/.test(zip.trim());
  const canSubmit =
    name.trim().length > 0 && emailIsValid && zipIsValid && !submitting;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setInlineError("");
    try {
      // In placeholder mode the backend accepts any non-empty token.
      const turnstileToken = isTurnstileConfigured()
        ? "cf-turnstile-token-todo"  // real Turnstile widget wiring is a follow-up
        : "placeholder-mode-token";
      const r = await api.post("/waitlist/join", {
        name: name.trim(),
        email: email.trim().toLowerCase(),
        zip: zip.trim(),
        phone: phone.trim() || null,
        address: address.trim() || null,
        marketing_opt_in: waitlistMarketingOptIn,
        source: "landing",
        turnstile_token: turnstileToken,
      });
      setConfirmation(r.data);
      // Commit B — §4.2. Fires on genuine POST-success. Zip is
      // safe to include (it's public geo — used for §3.4-style
      // regional segmentation on the dashboard).
      trackEvent("waitlist.join", { zip: zip.trim() });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.error : null;
      if (code === "invalid_email" || code === "invalid_zip" || code === "invalid_name") {
        setInlineError(
          (typeof detail === "object" && detail?.message) ||
            formatApiErrorDetail(detail),
        );
      } else {
        toast.error(
          formatApiErrorDetail(detail) ||
            "Couldn't join the waitlist. Please try again.",
        );
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (confirmation) {
    return (
      <div
        className="rounded-3xl border border-border bg-pp-surface p-8 text-center"
        data-testid="waitlist-confirmation"
      >
        <CheckCircle2 className="mx-auto h-10 w-10 text-brand-orange" aria-hidden="true" />
        <h3 className="mt-4 font-heading text-xl font-bold text-pp-text">
          {confirmation.already_joined ? "You're already on the list" : "You're on the list"}
        </h3>
        <p
          className="mt-2 text-sm text-muted-foreground"
          data-testid="waitlist-confirmation-message"
        >
          {`We'll email you when booking opens in ${confirmation.zip}.`}
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-3xl border border-border bg-pp-surface p-6 sm:p-8"
      data-testid="waitlist-form"
      id="waitlist"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <Label htmlFor="wl-name" className="text-sm font-semibold text-pp-text">
            Your name <span className="text-brand-orange">*</span>
          </Label>
          <Input
            id="wl-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={120}
            required
            className="mt-2 h-11 rounded-full border-border bg-pp-surface-2 px-5"
            data-testid="waitlist-name-input"
          />
        </div>
        <div>
          <Label htmlFor="wl-email" className="text-sm font-semibold text-pp-text">
            Email <span className="text-brand-orange">*</span>
          </Label>
          <Input
            id="wl-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="mt-2 h-11 rounded-full border-border bg-pp-surface-2 px-5"
            data-testid="waitlist-email-input"
          />
        </div>
        <div>
          <Label htmlFor="wl-zip" className="text-sm font-semibold text-pp-text">
            ZIP code <span className="text-brand-orange">*</span>
          </Label>
          <Input
            id="wl-zip"
            inputMode="numeric"
            pattern="\d{5}"
            value={zip}
            onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
            required
            className="mt-2 h-11 rounded-full border-border bg-pp-surface-2 px-5"
            data-testid="waitlist-zip-input"
          />
        </div>
        <div>
          <Label htmlFor="wl-phone" className="text-sm font-semibold text-pp-text">
            Phone <span className="text-muted-foreground">(optional)</span>
          </Label>
          <Input
            id="wl-phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="mt-2 h-11 rounded-full border-border bg-pp-surface-2 px-5"
            data-testid="waitlist-phone-input"
          />
        </div>
        <div>
          <Label htmlFor="wl-address" className="text-sm font-semibold text-pp-text">
            Address <span className="text-muted-foreground">(optional)</span>
          </Label>
          <Input
            id="wl-address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            className="mt-2 h-11 rounded-full border-border bg-pp-surface-2 px-5"
            data-testid="waitlist-address-input"
          />
        </div>
      </div>

      {/* Marketing opt-in — SEPARATE from the join submission itself.
          UNCHECKED BY DEFAULT. Never bundled with the required fields.
          See load-bearing note at the top of this file. */}
      <div className="mt-5 flex items-start gap-3">
        <Checkbox
          id="wl-marketing"
          checked={waitlistMarketingOptIn}
          onCheckedChange={(v) => setWaitlistMarketingOptIn(v === true)}
          data-testid="waitlist-marketing-optin-checkbox"
        />
        <Label
          htmlFor="wl-marketing"
          className="text-sm text-muted-foreground"
          data-testid="waitlist-marketing-optin-text"
        >
          {LOCKED_MARKETING_LABEL}
        </Label>
      </div>

      {/* Turnstile — mirrors the pattern from
          `pages/partner/BusinessIntakePage.jsx`. When the site key
          isn't provisioned yet, we render a placeholder note and
          submit `placeholder-mode-token`; the backend's
          `verify_turnstile` accepts it. Once the key is provisioned,
          both surfaces graduate to the real widget in a single
          follow-up dispatch. */}
      <div
        className="mt-5 rounded-2xl border border-border bg-pp-surface-2 p-3"
        data-testid="waitlist-captcha-box"
      >
        {isTurnstileConfigured() ? (
          <p
            className="text-sm text-muted-foreground"
            data-testid="waitlist-captcha-configured-copy"
          >
            Cloudflare Turnstile widget renders here (configured).
          </p>
        ) : (
          <p
            className="text-sm text-muted-foreground"
            data-testid="waitlist-captcha-placeholder-copy"
          >
            Bot-check will be enabled once Cloudflare Turnstile is provisioned.
            Your submission is reviewed the same way in either case.
          </p>
        )}
      </div>

      {inlineError && (
        <p
          className="mt-4 text-sm text-brand-orange"
          data-testid="waitlist-inline-error"
        >
          {inlineError}
        </p>
      )}

      <Button
        type="submit"
        disabled={!canSubmit}
        className="mt-6 w-full rounded-full bg-pp-purple text-white hover:bg-pp-purple-dark sm:w-auto sm:min-w-[220px]"
        data-testid="waitlist-submit"
      >
        {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
        Join the waitlist
      </Button>
    </form>
  );
}
